"use client";

import {
  Banknote,
  CalendarClock,
  ChevronRight,
  ChevronsUpDown,
  Database,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  UserCheck,
  BarChart3,
  Table,
  Settings,
  PieChart,
  MessageSquareWarning,
  FileText,
} from "lucide-react";
import * as React from "react";
import { Link, useLocation } from "react-router";
import Cookies from "js-cookie";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { ROLES } from "@/config/roles";
import { useAuth } from "@/hooks/use-auth";

interface MenuItem {
  title: string;
  url: string;
  icon?: React.ElementType;
  allowedRoles: string[];
  items?: MenuItem[];
}

export default function AppSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation();
  const { user } = useAuth();

  const handleLogout = () => {
    Cookies.remove("auth_token", { path: "/" });
    window.location.href = `${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/logout`;
  };

  // 1. Definisikan Menu Berdasarkan Role
  const mainMenus: MenuItem[] = [
    {
      title: "Dashboard",
      url: "/admin/dashboard",
      icon: LayoutDashboard,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV],
    },
    {
      title: "Sebaran SKS",
      url: "/admin/dashboard/sebaran-sks",
      icon: BarChart3,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV],
    },
    {
      title: "Sebaran IKU",
      url: "/admin/dashboard/cluster-iku",
      icon: PieChart,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV],
    },
    {
      title: "Komparasi Remun 70% Lama",
      url: "/admin/dashboard/analytics-70-table",
      icon: Table,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV],
    },
    {
      title: "Remun Dosen",
      url: "/admin/remun-dosen",
      icon: Banknote,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV, ROLES.ADMIN_FAKULTAS],
    },
    {
      title: "Rekap Remun",
      url: "/admin/rekap-remun",
      icon: FileText,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV, ROLES.ADMIN_FAKULTAS],
    },
    {
      title: "Validasi Jabatan",
      url: "/admin/validasi-jabatan",
      icon: UserCheck,
      allowedRoles: [
        ROLES.ADMIN_SUPER,
        ROLES.ADMIN_KEPEGAWAIAN,
        ROLES.ADMIN_HTL,
      ],
    },
    {
      title: "Pertanyaan & Masukan",
      url: "/admin/pertanyaan",
      icon: MessageSquareWarning,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_UNIV],
    },
    {
      title: "Dashboard",
      url: "/dosen/dashboard",
      icon: LayoutDashboard,
      allowedRoles: [ROLES.DOSEN],
    },
    {
      title: "Remunerasi Saya",
      url: "/dosen/remunerasi",
      icon: Banknote,
      allowedRoles: [ROLES.DOSEN],
    },
  ];

  const periodeMenus: MenuItem[] = [
    {
      title: "Manajemen Periode",
      url: "#",
      icon: CalendarClock,
      allowedRoles: [ROLES.ADMIN_SUPER],
      items: [
        {
          title: "Komponen",
          url: "/admin/periode/komponen",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        {
          title: "Grade",
          url: "/admin/periode/grade",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        {
          title: "Nilai Max/Unsur",
          url: "/admin/periode/nilai-max-unsur",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        {
          title: "Kelas Fakultas",
          url: "/admin/periode/kelas",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        // {
        //   title: "Cluster",
        //   url: "/admin/periode/cluster",
        //   allowedRoles: [ROLES.ADMIN_SUPER],
        // },
        // {
        //   title: "Besaran Serdos",
        //   url: "/admin/periode/besaran-serdos",
        //   allowedRoles: [ROLES.ADMIN_SUPER],
        // },
      ],
    },
  ];

  const masterDataMenus: MenuItem[] = [
    {
      title: "Master Data",
      url: "#",
      icon: Database,
      allowedRoles: [ROLES.ADMIN_SUPER],
      items: [
        {
          title: "Data Dosen",
          // Diperbaiki agar sesuai dengan nested route superadmin
          url: "/admin/master/dosen",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        // Menambahkan Komponen Kegiatan
        {
          title: "Komponen Kegiatan",
          url: "/admin/master/komponen-kegiatan",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        {
          title: "Unit Kerja",
          // Diperbaiki agar sesuai dengan nested route superadmin
          url: "/admin/master/unit-kerja",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        // Menambahkan Grade
        {
          title: "Grade",
          url: "/admin/master/grade",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        // Menambahkan Users
        {
          title: "Users",
          url: "/admin/master/users",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
      ],
    },
  ];

  const settingsMenus: MenuItem[] = [
    {
      title: "Pengaturan Sistem",
      url: "#",
      icon: Settings,
      allowedRoles: [ROLES.ADMIN_SUPER],
      items: [
        {
          title: "Konfigurasi CAS",
          url: "/settings/cas",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
        {
          title: "Log Aktivitas",
          url: "/settings/logs",
          allowedRoles: [ROLES.ADMIN_SUPER],
        },
      ],
    },
  ];

  // 2. Fungsi Filter Berdasarkan Role User
  const filterByRole = (menuList: MenuItem[]) => {
    return menuList
      .map((menu) => {
        const hasAccess = menu.allowedRoles.includes(user?.role || "");

        if (menu.items) {
          const filteredSubItems = menu.items.filter((sub) =>
            sub.allowedRoles.includes(user?.role || ""),
          );
          if (filteredSubItems.length > 0) {
            return { ...menu, items: filteredSubItems };
          }
        }

        return hasAccess ? menu : null;
      })
      .filter((menu): menu is MenuItem => menu !== null);
  };

  const filteredMain = filterByRole(mainMenus);
  const filteredMaster = filterByRole(masterDataMenus);
  const filteredSettings = filterByRole(settingsMenus);
  const filteredPeriode = filterByRole(periodeMenus);

  return (
    <Sidebar variant="floating" collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-white">
                <GraduationCap className="size-5" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">REMUNERASI</span>
                <span className="truncate text-xs">Undiksha</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Grup Menu Utama */}
        <SidebarGroup>
          <SidebarGroupLabel>Menu Utama</SidebarGroupLabel>
          <SidebarMenu>
            {filteredMain.map((item) => (
              <SidebarMenuItem key={item.title}>
                <Link to={item.url}>
                  <SidebarMenuButton
                    isActive={pathname === item.url}
                    tooltip={item.title}
                  >
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {filteredPeriode.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Konfigurasi Remunerasi</SidebarGroupLabel>
            <SidebarMenu>
              {filteredPeriode.map((item) => (
                <Collapsible key={item.title} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger className="w-full" asChild>
                      <SidebarMenuButton tooltip={item.title}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map((sub) => (
                          <SidebarMenuSubItem key={sub.title}>
                            <Link to={sub.url}>
                              <SidebarMenuSubButton
                                isActive={pathname === sub.url}
                              >
                                {sub.title}
                              </SidebarMenuSubButton>
                            </Link>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {/* Grup Master Data (Collapsible) */}
        {filteredMaster.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Data Master</SidebarGroupLabel>
            <SidebarMenu>
              {filteredMaster.map((item) => (
                <Collapsible key={item.title} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger className="w-full" asChild>
                      <SidebarMenuButton tooltip={item.title}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map((sub) => (
                          <SidebarMenuSubItem key={sub.title}>
                            <SidebarMenuSubButton
                              isActive={pathname === sub.url}
                            >
                              <Link to={sub.url}>{sub.title}</Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {/* Grup Pengaturan */}
        {filteredSettings.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Sistem</SidebarGroupLabel>
            <SidebarMenu>
              {filteredSettings.map((item) => (
                <Collapsible key={item.title} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger className="w-full" asChild>
                      <SidebarMenuButton tooltip={item.title}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map((sub) => (
                          <SidebarMenuSubItem key={sub.title}>
                            <SidebarMenuSubButton
                              isActive={pathname === sub.url}
                            >
                              <Link to={sub.url}>{sub.title}</Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger className="w-full" asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-secondary/80 text-primary font-bold">
                      {user?.name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user?.name}</span>
                    <span className="truncate text-xs capitalize text-muted-foreground">
                      {user?.role}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-56 rounded-lg"
                side="top"
                align="start"
                sideOffset={4}
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.username}
                    </p>
                    {/* <p className="text-xs leading-none text-muted-foreground">
                      ID: {user?.id}
                    </p> */}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="mr-2 size-4" />
                  Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
